import api from './api';
import { Promotion, PromotionType, PromotionSchemeType, InvoicePage, MarketingProductVariant } from '../types/marketingTypes';
import { ProductVariant as BaseProductVariant } from '../types/product';

// وظيفة مساعدة لتحويل متغير المنتج من الشكل الأساسي إلى الشكل المطلوب للتسويق
function convertToMarketingVariant(variant: BaseProductVariant, productName?: string): MarketingProductVariant {
  // استخراج أول باركود (إذا وجد)
  const firstBarcode = variant.barcodes && variant.barcodes.length > 0
    ? variant.barcodes[0].code
    : '';
  
  // تحويل سجل السمات إلى مصفوفة
  const attributesArray = Object.entries(variant.attributes || {}).map(([name, value]) => ({
    name,
    value
  }));
  
  // إعداد بيانات السعر (من أول باركود إذا وجد)
  const price = variant.barcodes && variant.barcodes.length > 0
    ? variant.barcodes[0].retailPrice
    : 0;
  
  const costPrice = 0; // ليس متوفرًا في النموذج الأساسي
  
  return {
    _id: variant._id || '',
    productId: '', // سيتم تعبئته من الخادم إذا كان متاحاً
    productName: productName || '', // اسم المنتج من المعامل
    sku: variant.sku || '',
    barcode: firstBarcode,
    attributes: attributesArray,
    price,
    costPrice,
    stock: variant.quantity || 0,
    unit: variant.unit || '' // الوحدة من المتغير
  };
}

// خدمات الحملات التسويقية
const promotionService = {
  // جلب جميع الحملات التسويقية
  getAllPromotions: async () => {
    const response = await api.get('/marketing/promotions');
    return response.data.promotions || [];
  },

  // جلب الحملات حسب النوع (تلقائية أو كوبون)
  getPromotionsByType: async (type: PromotionType) => {
    const response = await api.get(`/marketing/promotions/type/${type}`);
    return response.data;
  },

  // جلب حملة تسويقية بواسطة المعرف
  getPromotionById: async (id: string) => {
    const response = await api.get(`/marketing/promotions/${id}`);
    return response.data;
  },

  // إنشاء حملة تسويقية جديدة
  createPromotion: async (promotion: Omit<Promotion, '_id' | 'createdAt' | 'updatedAt'>) => {
    const response = await api.post('/marketing/promotions', promotion);
    return response.data;
  },

  // تحديث حملة تسويقية
  updatePromotion: async (id: string, promotion: Partial<Promotion>) => {
    const response = await api.put(`/marketing/promotions/${id}`, promotion);
    return response.data;
  },

  // حذف حملة تسويقية
  deletePromotion: async (id: string) => {
    const response = await api.delete(`/marketing/promotions/${id}`);
    return response.data;
  },

  // جلب الحملات النشطة
  getActivePromotions: async () => {
    const response = await api.get('/marketing/promotions/active');
    return response.data;
  },

  // جلب الحملات النشطة لصفحة فاتورة محددة
  getActivePromotionsForInvoicePage: async (invoicePageId: string) => {
    const response = await api.get(`/marketing/promotions/active/page/${invoicePageId}`);
    return response.data;
  },

  // التحقق من صلاحية كوبون
  validateCoupon: async (code: string, invoicePageId: string) => {
    const response = await api.post('/marketing/promotions/validate-coupon', { code, invoicePageId });
    return response.data;
  },

  // تطبيق كوبون مع بيانات الفاتورة
  applyCouponToInvoice: async (code: string, invoicePageId: string, invoiceData: any) => {
    const response = await api.post('/coupons/verify', {
      code,
      pageId: invoicePageId,
      invoice: invoiceData
    });
    return response.data;
  },

  // جلب جميع صفحات الفواتير
  getAllInvoicePages: async (): Promise<InvoicePage[]> => {
    try {
      const response = await api.get('/marketing/promotions/invoice-pages');
      const pages = response.data.data.pages || [];
      return pages.map((page: any) => ({
        _id: page._id || '',
        sequentialId: page.sequentialId || 0,
        name: page.name,
        type: page.type,
        enableMarketing: page.enableMarketing || false,
        createdAt: page.createdAt ? new Date(page.createdAt) : new Date(),
        updatedAt: page.updatedAt ? new Date(page.updatedAt) : new Date()
      }));
    } catch (error) {
      console.error('Error fetching invoice pages:', error);
      return [];
    }
  },

  // جلب صفحات الفواتير المفعلة للتسويق فقط
  getMarketingEnabledInvoicePages: async (): Promise<InvoicePage[]> => {
    try {
      const response = await api.get('/marketing/promotions/marketing-enabled-pages');
      const pages = response.data.data.pages || [];
      return pages.map((page: any) => ({
        _id: page._id || '',
        sequentialId: page.sequentialId || 0,
        name: page.name,
        type: page.type,
        enableMarketing: page.enableMarketing || false,
        createdAt: page.createdAt ? new Date(page.createdAt) : new Date(),
        updatedAt: page.updatedAt ? new Date(page.updatedAt) : new Date()
      }));
    } catch (error) {
      console.error('Error fetching marketing enabled invoice pages:', error);
      return [];
    }
  },

  // استخدام كوبون
  useCoupon: async (code: string) => {
    const response = await api.post(`/marketing/promotions/use-coupon/${code}`);
    return response.data;
  },
  
  // جلب متغير منتج بواسطة المعرف
  getVariantById: async (id: string): Promise<MarketingProductVariant | null> => {
    try {
      // استخدام المسار المباشر للحصول على متغير المنتج بواسطة المعرف
      const response = await api.get(`/products/variants/${id}`);
      if (response.data) {
        // تحويل متغير المنتج من الشكل الأساسي إلى الشكل المطلوب للتسويق
        const variant = response.data as BaseProductVariant;
        const productName = response.data.productName || ''; // استخراج اسم المنتج من الاستجابة
        return convertToMarketingVariant(variant, productName);
      }
      return null;
    } catch (error) {
      console.error('Error fetching variant by id:', error);
      return null;
    }
  },
  
  // جلب متغير منتج بواسطة الباركود
  getVariantByBarcode: async (barcode: string): Promise<MarketingProductVariant | null> => {
    try {
      // ترميز الباركود لحل مشكلة الشرطات المائلة والرموز الخاصة
      const encodedBarcode = encodeURIComponent(barcode);
      const response = await api.get(`/products/variants/barcode/${encodedBarcode}`);
      console.log('Response from server for barcode:', barcode, response.data);
      
      if (response.data && response.data.success && response.data.variant) {
        const variantData = response.data.variant;
        
        // تحويل البيانات إلى تنسيق MarketingProductVariant
        const marketingVariant: MarketingProductVariant = {
          _id: variantData.variantId || '',
          productId: variantData.productId || '',
          productName: variantData.productName || '',
          sku: variantData.barcode || '', // استخدام الباركود كـ SKU مؤقتاً
          barcode: variantData.barcode || '',
          attributes: Object.entries(variantData.variantAttributes || {}).map(([name, value]) => ({
            name,
            value: String(value)
          })),
          price: 0, // سيتم تحديثه لاحقاً من بيانات الباركود
          costPrice: 0,
          stock: 0, // سيتم تحديثه لاحقاً
          unit: variantData.unitName || variantData.unitId || ''
        };
        
        console.log('Converted marketing variant:', marketingVariant);
        return marketingVariant;
      }
      return null;
    } catch (error) {
      console.error('Error fetching variant by barcode:', error);
      return null;
    }
  },
  
  // جلب متغيرات منتج معين
  getProductVariants: async (productId: string): Promise<MarketingProductVariant[]> => {
    try {
      // أولاً نحصل على معلومات المنتج لنحصل على اسمه
      const productResponse = await api.get(`/products/${productId}`);
      const productName = productResponse.data?.name || '';
      
      // ثم نحصل على متغيرات المنتج
      const response = await api.get(`/products/${productId}/variants`);
      if (response.data && Array.isArray(response.data)) {
        // تحويل متغيرات المنتج مع إضافة اسم المنتج
        return response.data.map((variant: BaseProductVariant) => {
          const marketingVariant = convertToMarketingVariant(variant);
          marketingVariant.productName = productName; // إضافة اسم المنتج
          marketingVariant.productId = productId; // إضافة معرف المنتج
          return marketingVariant;
        });
      }
      return [];
    } catch (error) {
      console.error('Error fetching product variants:', error);
      return [];
    }
  },
  
  // استيراد متغيرات المنتج من ملف إكسل (قائمة باركودات)
  importProductVariantsFromExcel: async (formData: FormData) => {
    try {
      const file = formData.get('file') as File;
      console.log('File info:', file.name, file.type, file.size);
      let barcodes: string[] = [];
      
      if (file.name.endsWith('.csv')) {
        // قراءة ملف CSV
        const text = await file.text();
        console.log('CSV content:', text);
        const lines = text.split('\n').filter(line => line.trim());
        // تجاهل السطر الأول إذا كان يحتوي على عنوان
        barcodes = lines.slice(1).map(line => line.trim()).filter(barcode => barcode);
      } else {
        // قراءة ملف Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = await import('xlsx').then(XLSX => XLSX.read(arrayBuffer, { type: 'array' }));
        const sheetName = workbook.SheetNames[0];
        console.log('Sheet name:', sheetName);
        const worksheet = workbook.Sheets[sheetName];
        const data = await import('xlsx').then(XLSX => XLSX.utils.sheet_to_json(worksheet, { header: 1 })) as string[][];
        console.log('Excel data:', data);
        
        // استخراج الباركودات من العمود الأول (تجاهل السطر الأول)
        barcodes = data.slice(1)
          .map(row => row[0])
          .filter(barcode => barcode && typeof barcode === 'string')
          .map(barcode => barcode.toString().trim());
      }
      
      console.log('Extracted barcodes:', barcodes);
      
      // البحث عن كل باركود والحصول على معلومات المنتج
      const variants: MarketingProductVariant[] = [];
      const failedBarcodes: string[] = [];
      
      for (const barcode of barcodes) {
        try {
          console.log(`Searching for barcode: ${barcode}`);
          const variant = await promotionService.getVariantByBarcode(barcode);
          if (variant) {
            console.log(`Found variant for barcode ${barcode}:`, variant);
            variants.push(variant);
          } else {
            console.warn(`No variant found for barcode: ${barcode}`);
            failedBarcodes.push(barcode);
          }
        } catch (error) {
          console.error(`Error searching for barcode ${barcode}:`, error);
          failedBarcodes.push(barcode);
        }
      }
      
      console.log('Import results:', {
        totalBarcodes: barcodes.length,
        successfulImports: variants.length,
        failedBarcodes
      });
      
      return {
        success: true,
        message: `تم استيراد ${variants.length} منتج من أصل ${barcodes.length} باركود${failedBarcodes.length > 0 ? ` (فشل في: ${failedBarcodes.join(', ')})` : ''}`,
        variants
      };
    } catch (error) {
      console.error('Error importing variants from Excel:', error);
      throw new Error('حدث خطأ أثناء استيراد البيانات');
    }
  }
};

export default promotionService;